import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { getUploadLimits } from "@serie3/common/api/chunkedUpload";
import Logo from "@serie3/mymediaplanner/assets/images/mymediaplanner.webp";
import { useUploads } from "../Upload/useUploads";
import {
  STATUS,
  isActive,
  summarize,
  enqueue,
  addRejected,
  cancel,
  retry,
  dismiss,
  clearFinished,
} from "../Upload/uploadManager";
import { formatBytes } from "../Upload/UploadStatusWidget";

const FALLBACK_MAX_BYTES = 2 * 1024 * 1024 * 1024;

const wrapperClass = (entry) => {
  switch (entry.status) {
    case STATUS.DONE:
      return "dropzoneImageWrapperOk";
    case STATUS.FAILED:
      return "dropzoneImageWrapperFail";
    case STATUS.TOO_BIG:
      return "dropzoneImageWrapperFileToBig";
    case STATUS.CANCELLED:
      return "dropzoneImageWrapperCancelled";
    default:
      return "dropzoneImageWrapper";
  }
};

/**
 * Drop files, they upload through the page-wide upload manager. Closing the
 * surrounding modal does not stop anything: reopening it shows the same list
 * (per `scope`), and the UploadStatusWidget in the layout shows it meanwhile.
 *
 * `onUploaded(doc)` gets the API's document for every finished file. Use a
 * functional state update in it: it may fire long after the drop, after other
 * changes to the same list.
 */
const FileDropzone = ({
  uploadUrl = "/documents/",
  additionalData = {},
  edit = false,
  setName = true,
  onUploaded,
  setShowPayModal = null,
  scope = null,
}) => {
  const { t } = useTranslation();
  const listScope = scope ?? uploadUrl;
  const uploads = useUploads(listScope);
  const summary = summarize(uploads);
  const [location, setLocation] = useState();
  const [maxBytes, setMaxBytes] = useState(FALLBACK_MAX_BYTES);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    setLocation(window.location.hostname);
    getUploadLimits().then((limits) => {
      if (mounted.current && Number.isFinite(limits.max_total_bytes)) {
        setMaxBytes(limits.max_total_bytes);
      }
    });
    return () => {
      mounted.current = false;
    };
  }, []);

  const onDrop = (acceptedFiles, fileRejections) => {
    // the target is captured per file, so a later folder change or modal close cannot redirect it
    const target = {
      uploadUrl,
      additionalData,
      isEdit: edit === true,
      setName,
      onUploaded,
      setShowPayModal,
    };
    acceptedFiles.forEach((file) => enqueue(file, listScope, target));
    fileRejections.forEach((rejection) => {
      const tooBig = (rejection.errors || []).some((error) => error.code === "file-too-large");
      addRejected(rejection.file, listScope, tooBig ? STATUS.TOO_BIG : STATUS.FAILED);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: maxBytes,
    onDrop,
  });

  if (location === "demo.freelancersplace.net") {
    return "File upload is disabled on the demo system";
  }

  const stop = (event) => {
    // buttons sit inside the dropzone root; a click must not open the file picker
    event.stopPropagation();
  };

  const statusText = (entry) => {
    switch (entry.status) {
      case STATUS.QUEUED:
        return t("Backend.Upload.Status.Queued", { defaultValue: "waiting" });
      case STATUS.UPLOADING:
        return `${Math.round(entry.progress * 100)}%`;
      case STATUS.SAVING:
        return t("Backend.Upload.Status.Saving", { defaultValue: "saving" });
      case STATUS.DONE:
        return t("Backend.Upload.Status.Done", { defaultValue: "uploaded" });
      case STATUS.TOO_BIG:
        return t("Backend.Upload.Status.TooBigMax", {
          defaultValue: "too big, max. {{max}}",
          max: formatBytes(maxBytes),
        });
      case STATUS.CANCELLED:
        return t("Backend.Upload.Status.Cancelled", { defaultValue: "cancelled" });
      default:
        return t("Backend.Upload.Status.Failed", { defaultValue: "upload failed" });
    }
  };

  const tiles = uploads.map((entry) => {
    const isImage = entry.type.startsWith("image/");
    const isTextOrPdf = entry.type === "text/plain" || entry.type === "application/pdf";
    const active = isActive(entry);

    return (
      <figure key={entry.id} className={`dropzoneTile ${wrapperClass(entry)}`} title={entry.name}>
        <div className="dropzoneTilePreview">
          {isImage && entry.preview && <img src={entry.preview} className="dropzoneImage" alt="" />}
          {isTextOrPdf && entry.preview && <object data={entry.preview} aria-label="preview" />}
          {!(isImage && entry.preview) && !(isTextOrPdf && entry.preview) && (
            <div className="dropzoneTileNoPreview">
              <img src={Logo} alt="" />
              <b>{entry.name}</b>
            </div>
          )}
        </div>
        {active && (
          <div className="dropzoneTileBusy">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}
        <div className="uploadProgress dropzoneTileProgress">
          <div
            className="uploadProgressBar"
            style={{ width: `${entry.status === STATUS.DONE ? 100 : Math.round(entry.progress * 100)}%` }}
          />
        </div>
        <figcaption className="dropzoneTileCaption">
          <span className="dropzoneTileName">{entry.name}</span>
          <span className="dropzoneTileStatus">{statusText(entry)}</span>
          {active && (
            <button
              type="button"
              className="dropzoneTileButton"
              title={t("Backend.Upload.Cancel", { defaultValue: "Cancel upload" })}
              onClick={(event) => {
                stop(event);
                cancel(entry.id);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
          {(entry.status === STATUS.FAILED || entry.status === STATUS.CANCELLED) && (
            <button
              type="button"
              className="dropzoneTileButton"
              title={t("Backend.Upload.Retry", { defaultValue: "Retry" })}
              onClick={(event) => {
                stop(event);
                retry(entry.id);
              }}
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          )}
          {!active && (
            <button
              type="button"
              className="dropzoneTileButton"
              title={t("Backend.Upload.Remove", { defaultValue: "Remove from list" })}
              onClick={(event) => {
                stop(event);
                dismiss(entry.id);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </figcaption>
      </figure>
    );
  });

  return (
    <div className="dropzoneWithStatus">
      {uploads.length > 0 && (
        <div className={`dropzoneStatus ${summary.active > 0 ? "dropzoneStatusActive" : ""}`}>
          <div className="dropzoneStatusLine">
            {summary.active > 0 ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />{" "}
                <b>
                  {t("Backend.Upload.StillUploading", {
                    defaultValue: "{{count}} file(s) still uploading",
                    count: summary.active,
                  })}
                </b>
                {" · "}
                {t("Backend.Upload.CloseHint", {
                  defaultValue: "You can close this window, the uploads continue in the background.",
                })}
              </>
            ) : (
              <>
                <b>
                  {t("Backend.Upload.AllDone", {
                    defaultValue: "{{done}} uploaded, {{failed}} failed",
                    done: summary.done,
                    failed: summary.failed,
                  })}
                </b>
                <button
                  type="button"
                  className="btn btn-sm btn-link"
                  onClick={() => clearFinished(listScope)}
                >
                  {t("Backend.Upload.ClearList", { defaultValue: "Clear list" })}
                </button>
              </>
            )}
          </div>
          {summary.active > 0 && (
            <div className="uploadProgress">
              <div className="uploadProgressBar" style={{ width: `${Math.round(summary.progress * 100)}%` }} />
            </div>
          )}
        </div>
      )}
      <div {...getRootProps()} className="defaultDropzone">
        <input {...getInputProps()} />
        {!isDragActive && uploads.length > 0 && (
          <div className="dropzoneTiles">
            {tiles}
            <div className="dropzoneTile dropzoneTileAdd">
              <span className="btn btn-primary dropzoneButton">
                {t("Backend.Upload.AddMore", { defaultValue: "add more" })}
              </span>
            </div>
          </div>
        )}
        {!isDragActive && uploads.length === 0 && (
          <span className="dropzoneText">
            <p style={{ paddingTop: "100px" }}>
              {t("Backend.Upload.DropHere", { defaultValue: "Drag and drop files here" })}
              <br />
              {t("Backend.Upload.Or", { defaultValue: "or" })}
            </p>
            <p className="btn btn-primary dropzoneButton">
              {t("Backend.Upload.Select", { defaultValue: "select" })}
            </p>
          </span>
        )}
        {isDragActive && (
          <span className="dropzoneText">
            <p style={{ paddingTop: "100px" }}>
              {t("Backend.Upload.DropNow", { defaultValue: "Drop to upload" })}
            </p>
          </span>
        )}
      </div>
    </div>
  );
};

export default FileDropzone;
